<template>
  <b-modal id="add-company" title="Add Company" ref="modal" ok-title="Add" @ok="handleOk" @show="onReset"
    :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons" :no-close-on-backdrop="true" size="lg">
    <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />
    <b-form @submit.stop.prevent="handleSubmit" novalidate>
      <b-container fluid>
        <b-tabs content-class="mt-3">
          <b-tab title="Primary Details" active>
            <b-row>
              <b-col sm="12" md="6">
                <b-form-group label="Name" label-for="name"
                  description="Please enter the non-abbreviated name for the company">
                  <span v-show="errors.has('Name')" class="help-block">
                    {{ errors.first("Name") }}
                  </span>
                  <CInput name="Name" v-model="form.name" maxlength="30" v-validate="{
                    required: true,
                    regex: nameRegex,
                  }" />
                </b-form-group>
              </b-col>

              <b-col sm="12" md="6" class="mb-3" v-if="loggedUser.type == 'Admin'">
                <b-form-group label="Has Parent Company?">
                  <span v-show="errors.has('HasParentCompany')" class="help-block">{{ errors.first("HasParentCompany")
                  }}</span>
                  <b-form-radio-group v-model="form.hasParentCompany" :options="boolOptions" plain name="HasParentCompany"
                    v-validate="'required'" />
                </b-form-group>

                <b-form-group label="Parent Company" label-for="Parent Company" v-if="form.hasParentCompany">
                  <span v-show="errors.has('Parent Company')" class="help-block">{{ errors.first("Parent Company")
                  }}</span>
                  <b-form-select name="Parent Company" v-model="selCompany" v-validate="'selectRequired'"
                    :options="companyOptions" />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="12" md="6">
                <b-form-group label="Description" label-for="description" description>
                  <span v-show="errors.has('Description')" class="help-block">
                    {{ errors.first("Description") }}
                  </span>
                  <CTextarea name="Description" type="text" v-model="form.description" maxlength="200" :rows="3"
                    v-validate="{
                      regex: descriptionRegex,
                    }" />
                </b-form-group>
              </b-col>

              <b-col sm="12" md="6">
                <b-form-group label="Address" label-for="address" description>
                  <span v-show="errors.has('Address')" class="help-block">
                    {{ errors.first("Address") }}
                  </span>
                  <CTextarea name="Address" type="text" v-model="form.address" maxlength="200" :rows="3" v-validate="{
                    required: true,
                    regex: addressRegex,
                  }" />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row v-if="loggedUser.type == 'Admin'">
              <b-col sm="12" md="6">
                <b-form-group label="Is an LGU?" description="*LGU = Local Government Unit">
                  <span v-show="errors.has('isLgu')" class="help-block">
                    {{ errors.first("isLgu") }}
                  </span>
                  <b-form-radio-group v-model="form.isLgu" :options="boolOptions" plain name="isLgu"
                    v-validate="'required'" />
                </b-form-group>

                <b-form-group label="LGU Keyword" v-if="form.isLgu">
                  <span v-show="errors.has('LGU Keyword')" class="help-block">
                    {{ errors.first("LGU Keyword") }}
                  </span>
                  <CInput name="LGU Keyword" v-model="form.lguKeyword"
                    description="This will be used to match on user's address to determine it's citizens. (i.e. Pakil)"
                    v-validate="{
                      required: true,
                      regex: /(?!^\d+$)^.+$/,
                    }" />
                </b-form-group>
              </b-col>

              <b-col sm="12" md="6" v-if="form.isLgu">
                <b-form-group label="LGU Tagline Logo" label-for="tagline-logo" description="*32-bit PNG, up to 1 MB">
                  <b-form-file id="tagline-logo" placeholder="Choose image" ref="taglineImagefileInput" accept="image/png"
                    @change="onSelectTaglineLogo">
                    <template slot="file-name" slot-scope="{ names }">
                      <b-badge variant="dark">{{ names[0] }}</b-badge>
                      <b-badge v-if="names.length > 1" variant="dark" class="ml-1">+ {{ names.length - 1 }} More
                        files</b-badge>
                    </template>
                  </b-form-file>
                </b-form-group>

                <!-- Preview Images -->
                <div v-if="taglineLogo.url">
                  <b-card overlay :img-src="taglineLogo.url" img-alt="Card Image" text-variant="white" class="mb-0">
                    <b-card-text class="text-right">
                      <b-button variant="danger" size="sm" @click="onRemoveTaglineLogo">
                        <CIcon name="cil-trash" />
                      </b-button>
                    </b-card-text>
                  </b-card>
                  <b-progress v-if="uploading.uploadStatus === 'uploading'" :value="uploading.percentage"
                    :max="uploading.filename" variant="success" height="8px" animated />
                </div>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="Subscription">
            <b-row>
              <b-col sm="12" md="6" class="mb-3">
                <b-form-group label="Scan Quota" label-for="scanQuota">
                  <CInput name="scanQuota" type="number" min="0" v-model.number="form.scanQuota" />
                </b-form-group>
              </b-col>
              <b-col sm="12" md="6" class="mb-3">
                <b-form-group label="Health Declaration Form Language" label-for="hdfLanguage"
                  description="*Health declaration form dialect during employee check-in">
                  <b-form-radio-group name="hdfLanguage" v-model="form.hdfLanguage" :options="langOptions" plain />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="12" md="6">
                <b-form-group label="Company Logo" label-for="company-logo"
                  description="*32-bit PNG, 300px by 300px, up to 1 MB">
                  <b-form-file id="company-logo" placeholder="Choose image" ref="companyLogofileInput" accept="image/png"
                    @change="onSelectCompanyLogo($event)">
                    <template slot="file-name" slot-scope="{ names }">
                      <b-badge variant="dark">{{ names[0] }}</b-badge>
                      <b-badge v-if="names.length > 1" variant="dark" class="ml-1">+ {{ names.length - 1 }} More
                        files</b-badge>
                    </template>
                  </b-form-file>
                </b-form-group>

                <!-- Image Preview -->
                <div v-if="companyLogo.url">
                  <b-card overlay :img-src="companyLogo.url" img-alt="Card Image" text-variant="white" class="mb-0">
                    <b-card-text class="text-right">
                      <b-button variant="danger" size="sm" @click="onRemoveCompanyLogo()">
                        <CIcon name="cil-trash" />
                      </b-button>
                    </b-card-text>
                  </b-card>
                  <b-progress v-if="uploading.uploadStatus === 'uploading'" :value="uploading.percentage"
                    :max="uploading.filename" variant="success" height="8px" animated />
                </div>
              </b-col>
              <b-col sm="12" md="6">
                <b-form-group label="Status">
                  <b-form-radio-group v-model="form.isActive" :options="statusOptions" plain name="Status" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-container>
    </b-form>
  </b-modal>
</template>

<script>
// Util
import { ValidationUtil } from "@/utils/validationUtil";
import { DropDownItemsUtil } from "@/utils/dropDownItemsUtil";
import { DateUtil } from '@/utils/dateUtil';

// API & DAO
import companyApi from "@/api/companyApi";
import companiesDAO from "@/database/companies";

// Others
import config from "@/config/env-constants";
import EventBus from "@/shared/event-bus";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { storage } from "@/config/firebase";

export default {
  name: "add-company",
  components: {
    Loading,
  },
  props: {
    allCompaniesObj: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        name: "",
        address: "",
        description: "",
        hasParentCompany: false,
        parentCompany: "",
        parentCompanyId: "",
        scanQuota: 8,
        hdfLanguage: "English",
        isLgu: false,
        lguKeyword: "",
        lguPic: {},
        companyLogo: {},
        isActive: true,
      },
      taglineLogo: {},
      companyLogo: {},
      uploading: {
        filename: "",
        percentage: 0,
        uploadStatus: "", // [uploading, success, error]
      },

      boolOptions: config.boolOptions,
      langOptions: config.langOptions,
      statusOptions: config.statusOptions,
      companyOptions: [],

      selCompany: { ...config.companyDefaultValue },

      loggedUser: this.$store.getters["auth/loggedUser"],
      loggedUserCompany: this.$store.getters["auth/loggedUserCompany"],
      // Check for loader
      isLoading: false,
    };
  },
  computed: {
    disableConfirmButtons() {
      return this.isLoading;
    },
    nameRegex() {
      return config.nameRegex;
    },
    addressRegex() {
      return config.addressRegex;
    },
    descriptionRegex() {
      return config.descriptionRegex;
    }
  },
  watch: {
    allCompaniesObj: function () {
      if (this.loggedUser.type === "Admin") {
        this.companyOptions = DropDownItemsUtil.retrieveCompanyItems(this.allCompaniesObj);
      }
    },
    "form.hasParentCompany": function () {
      if (!this.form.hasParentCompany) {
        this.form.parentCompany = "";
        this.form.parentCompanyId = "";
        this.selCompany = [...config.companyDefaultValue];
      }
    },
    "form.isLgu": function () {
      if (!this.form.isLgu) {
        this.form.lguKeyword = "";
      }
    },
  },
  mounted() {
    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      if (modalId === "add-company" && bvEvent.type === "show") {
        if (this.loggedUser.type === "Manager") {
          this.selCompany = DropDownItemsUtil.getCompanyItem(this.loggedUserCompany);
          this.form.hasParentCompany = true;
        }
      }
    });
  },
  methods: {
    updateParentCompanySelection() {
      if (this.selCompany.id && this.form.hasParentCompany) {
        this.form.parentCompany = this.selCompany.name;
        this.form.parentCompanyId = this.selCompany.id;
      } else {
        this.form.parentCompany = "";
        this.form.parentCompanyId = "";
      }
    },
    async handleOk(evt) {
      // Prevent modal from closing
      evt.preventDefault();

      let isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$toaster.warning("Please address the field/s with invalid input.");
        return;
      }

      // existing company
      if (this.hasExistingCompany()) {
        this.$toaster.warning("Company already exists.");
        return;
      }

      // has LGU keyword
      if (this.form.isLgu && this.hasExistingLguKeyword()) {
        this.$toaster.warning("LguKeyword already exist.");
        return;
      }

      // Removes excess whitespace
      this.form.name = ValidationUtil.removeExcessWhiteSpace(this.form.name);
      this.form.address = ValidationUtil.removeExcessWhiteSpace(this.form.address);
      this.form.description = ValidationUtil.removeExcessWhiteSpace(this.form.description);

      await this.handleSubmit();
    },
    hasExistingCompany() {
      let hasExisting = false;
      let companiesArr = Object.values(this.allCompaniesObj);
      for (const company of companiesArr) {
        if (
          company.name.toLowerCase().trim() ===
          this.form.name.toLowerCase().trim()
        ) {
          hasExisting = true;
          break;
        }
      }
      return hasExisting;
    },
    hasExistingLguKeyword() {
      let hasExisting = false;
      let companiesArr = Object.values(this.allCompaniesObj);
      for (const company of companiesArr) {
        if (
          company.lguKeyword &&
          company.lguKeyword.toLowerCase().trim() ===
          this.form.lguKeyword.toLowerCase().trim()
        ) {
          hasExisting = true;
          break;
        }
      }
      return hasExisting;
    },
    async handleSubmit() {
      let name = this.form.name;

      try {
        // show loading indicator
        this.isLoading = true;

        this.updateParentCompanySelection();

        let companyObj = { ... this.form };
        companyObj.dateCreated = DateUtil.getCurrentTimestamp();
        companyObj.updatedBy = this.loggedUser.email;
        companyObj.dateUpdated = DateUtil.getCurrentTimestamp();
        companyObj.updatedBy = this.loggedUser.email;

        let { data } = await companyApi.saveCompany(
          companyObj,
          this.loggedUser.email,
          DateUtil.getCurrentTimestamp()
        );

        if (data.isSuccess) {
          companyObj = { ...data.company };

          await this.uploadTaglineLogo(companyObj.id);
          await this.uploadCompanyLogo(companyObj.id);

          this.$toaster.success(`Company ${name} was created successfully.`);
          EventBus.$emit("onCloseSaveCompany", companyObj);
          this.$refs.modal.hide();
        } else {
          this.$toaster.warning(data.message);
        }

      } catch (_error) {
        this.$toaster.error(`Error creating company ${name}. Please try again.`);
      }

      // hide loading indicator
      this.isLoading = false;
    },
    async uploadTaglineLogo(docId) {
      const image = this.taglineLogo;
      if (image._isNew) {
        const result = await this.firebaseUploadImage(image, docId);
        await companiesDAO.updateLguTaglineLogo(docId, { lguPic: result });
        this.uploadingInProgress("success");
      }
    },
    async uploadCompanyLogo(docId) {
      const image = this.companyLogo;
      if (image._isNew) {
        const result = await this.firebaseUploadImage(image, docId);
        await companiesDAO.updateCompanyLogos(docId, { companyLogo: result });
        this.uploadingInProgress("success");
      }
    },
    async firebaseUploadImage(image, companyId) {
      let filename = "";

      if (image.fbStoragePath === config.fbStoragePath.COMPANY_LOGOS) {
        filename = `COMPANYLOGO_${Date.now()}.png`;
      } else if (image.fbStoragePath === config.fbStoragePath.TAGLINE_LOGOS) {
        filename = `LGU_${companyId}.png`;
      }

      this.uploadingInProgress("uploading", filename, 0);

      return new Promise((resolve, reject) => {
        let storageRef = storage.ref(`${image.fbStoragePath}/${filename}`);
        let task = storageRef.put(image.file);

        task.on(
          "state_changed",
          (snapshot) => {
            let percentage =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            this.uploadingInProgress("uploading", filename, percentage);
          },
          (error) => {
            reject(error);
            this.resetUploadingState();
          },
          () => {
            task.snapshot.ref.getDownloadURL().then((downloadURL) => {
              resolve({
                name: filename,
                url: downloadURL,
              });
            });
          }
        );
      });
    },
    uploadingInProgress(uploadStatus, filename = "", percentage = 0) {
      this.uploading.filename = filename;
      this.uploading.percentage = percentage;
      this.uploading.uploadStatus = uploadStatus;
    },
    resetUploadingState() {
      this.uploading = {
        filename: "",
        percentage: 0,
        uploadStatus: "",
      };
    },
    onRemoveTaglineLogo() {
      this.taglineLogo = {};
      this.$refs["taglineImagefileInput"].reset();
    },
    onRemoveCompanyLogo(index) {
      this.companyLogos.splice(index, 1);
      this.$refs["companyLogofileInput"].reset();
    },
    onSelectTaglineLogo(evt) {
      const file = evt.target.files[0];
      if (file.type === "image/png") {
        this.taglineLogo = {
          url: URL.createObjectURL(file),
          file: file,
          fbStoragePath: "images/lgus",
          _isNew: true,
        };
      } else {
        this.$refs["taglineImagefileInput"].reset();
        this.$toaster.warning("Only .PNG files are allowed.");
      }
    },
    onSelectCompanyLogo(evt) {
      const vm = this;
      const file = evt.target.files[0];
      const url = URL.createObjectURL(file);

      let dimensions = { w: 0, h: 0 };

      const image = new Image();
      image.onload = function () {
        dimensions.w = image.width;
        dimensions.h = image.height;

        if (dimensions.w > 300 || dimensions.h > 300) {
          vm.$toaster.warning("Company logo's width and height shouldn't be greater than 300 pixels");
        } else {
          vm.companyLogo = {
            url: url,
            file: file,
            fbStoragePath: "images/companyLogos",
            _isNew: true,
          };
        }
      };
      image.src = url;
    },
    onReset() {
      /* Reset our form values */
      this.form.name = "";
      this.form.address = "";
      this.form.description = "";
      this.form.hasParentCompany = false;
      this.form.parentCompany = "";
      this.form.parentCompanyId = "";
      this.form.scanQuota = 8;
      this.form.hdfLanguage = "English";
      this.form.isLgu = false;
      this.form.lguKeyword = "";
      this.form.lguPic = {};
      this.form.companyLogo = {};
      this.companyLogo = {};
      this.resetUploadingState();

      // reset validation
      this.$validator.reset();
      this.errors.clear();
    },
  },
};
</script>


